import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { CodeSentToast } from '../../components/common/CodeSentToast';
import { PasswordInput } from '../../components/common/PasswordInput';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'send' | 'reset'>('send');
  const [formData, setFormData] = useState({ email: '', verificationCode: '', newPassword: '', confirmPassword: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);

  const { sendVerificationCode, resetPassword, verificationState, resetPasswordState } = useAuthStore();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (countdown > 0) timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (resetPasswordState === 'success') navigate('/login');
  }, [resetPasswordState, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSendCode = async () => {
    if (countdown > 0) return;
    if (!formData.email) { setError('Please enter your email'); return; }
    try {
      setError('');
      await sendVerificationCode({ type: 'reset', email: formData.email });
      setCountdown(60);
      setStep('reset');
      setCodeSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send code');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (formData.newPassword !== formData.confirmPassword) { setError('Passwords do not match'); setIsLoading(false); return; }
    if (formData.newPassword.length < 6) { setError('Password must be at least 6 characters'); setIsLoading(false); return; }
    if (!formData.verificationCode) { setError('Please enter the verification code'); setIsLoading(false); return; }

    try {
      await resetPassword({
        verificationCode: formData.verificationCode,
        newPassword: formData.newPassword,
        email: formData.email,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 px-4">
      <CodeSentToast open={codeSent} onClose={() => setCodeSent(false)} />
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">{step === 'send' ? 'Reset password' : 'Set new password'}</h1>
        </div>

        <div className="card p-8">
          {step === 'send' ? (
            <div className="space-y-6">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input name="email" type="email" required className="input" placeholder="Your registered email" value={formData.email} onChange={handleChange} />
              </div>

              <button type="button" onClick={handleSendCode} disabled={countdown > 0 || verificationState === 'loading'} className="btn btn-primary w-full">
                {countdown > 0 ? `Resend in ${countdown}s` : 'Send code'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Verification code</label>
                <div className="flex space-x-2">
                  <input name="verificationCode" type="text" required className="input flex-1" placeholder="Enter the code" value={formData.verificationCode} onChange={handleChange} />
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={countdown > 0}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm min-w-[110px]"
                  >
                    {countdown > 0 ? `${countdown}s` : 'Resend'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New password</label>
                <PasswordInput
                  name="newPassword"
                  required
                  autoComplete="new-password"
                  placeholder="At least 6 characters"
                  value={formData.newPassword}
                  onChange={(v) => setFormData((f) => ({ ...f, newPassword: v }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm new password</label>
                <PasswordInput
                  name="confirmPassword"
                  required
                  autoComplete="new-password"
                  placeholder="Re-enter password"
                  value={formData.confirmPassword}
                  onChange={(v) => setFormData((f) => ({ ...f, confirmPassword: v }))}
                />
              </div>

              <button type="submit" disabled={isLoading} className="btn btn-primary w-full">
                {isLoading ? 'Resetting…' : 'Reset password'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-primary-600 hover:text-primary-500 font-medium">Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
